import * as React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Content,
  Divider,
  Dropdown,
  DropdownItem,
  DropdownList,
  FlexItem,
  MenuToggle,
  MenuToggleElement,
  Select,
  SelectOption,
  Title,
  Toolbar,
  ToolbarContent,
  ToolbarGroup,
  ToolbarItem,
} from '@patternfly/react-core';
import {
  CaretDownIcon,
  CodeBranchIcon,
  CogIcon,
  ExternalLinkAltIcon,
} from '@patternfly/react-icons';
import { MobileViewContext } from '@app/AppLayout/MobileViewContext';
import { getEdgeSettingsPaths } from '@app/utils/appContext';

const SERVICES = ['Charlie Services', 'Alpha Services', 'Beta Services'] as const;

export type PageContextActionsPlacement = 'title' | 'toolbar';

export interface PageContextActionsProps {
  /** `title` renders the mobile cog. `toolbar` renders Charlie Services + Settings on desktop. */
  placement: PageContextActionsPlacement;
}

const applySettingsAction = (
  value: string | number | undefined,
  navigate: ReturnType<typeof useNavigate>,
  pathname: string,
) => {
  if (value === 'add-repositories') {
    navigate(getEdgeSettingsPaths(pathname).repositories);
  } else if (value === 'authentications') {
    navigate(getEdgeSettingsPaths(pathname).authentications);
  }
};

export const PageContextActions: React.FunctionComponent<PageContextActionsProps> = ({ placement }) => {
  const isMobile = React.useContext(MobileViewContext);
  const location = useLocation();
  const navigate = useNavigate();
  const [isServiceOpen, setIsServiceOpen] = React.useState(false);
  const [selectedService, setSelectedService] = React.useState<(typeof SERVICES)[number]>('Charlie Services');
  const [isActionsOpen, setIsActionsOpen] = React.useState(false);
  const [isConfigOpen, setIsConfigOpen] = React.useState(false);

  const showTitleIcon = isMobile && placement === 'title';
  const showToolbar = !isMobile && placement === 'toolbar';

  if (!showTitleIcon && !showToolbar) {
    return null;
  }

  const onConfigSelect = (_event: React.MouseEvent<Element, MouseEvent> | undefined, value: string | number | undefined) => {
    setIsConfigOpen(false);
    if (typeof value === 'string' && (SERVICES as readonly string[]).includes(value)) {
      setSelectedService(value as (typeof SERVICES)[number]);
      return;
    }
    applySettingsAction(value, navigate, location.pathname);
  };

  const onServiceSelect = (_event: React.MouseEvent<Element, MouseEvent> | undefined, value: string | number | undefined) => {
    setSelectedService(value as (typeof SERVICES)[number]);
    setIsServiceOpen(false);
  };

  const onActionsSelect = (_event: React.MouseEvent<Element, MouseEvent> | undefined, value: string | number | undefined) => {
    setIsActionsOpen(false);
    applySettingsAction(value, navigate, location.pathname);
  };

  const serviceToggle = (toggleRef: React.Ref<MenuToggleElement>) => (
    <MenuToggle
      ref={toggleRef}
      onClick={() => setIsServiceOpen(!isServiceOpen)}
      isExpanded={isServiceOpen}
      variant="secondary"
      className="service-select-toggle"
    >
      {selectedService}
    </MenuToggle>
  );

  const actionsToggle = (toggleRef: React.Ref<MenuToggleElement>) => (
    <MenuToggle
      ref={toggleRef}
      onClick={() => setIsActionsOpen(!isActionsOpen)}
      isExpanded={isActionsOpen}
      variant="secondary"
      className="actions-menu-toggle"
      aria-label="Settings"
    >
      Settings <CaretDownIcon style={{ marginLeft: 'var(--pf-t--global--spacer--xs)' }} />
    </MenuToggle>
  );

  if (showTitleIcon) {
    return (
      <Dropdown
        isOpen={isConfigOpen}
        onOpenChange={(isOpen) => setIsConfigOpen(isOpen)}
        onSelect={onConfigSelect}
        toggle={(toggleRef: React.Ref<MenuToggleElement>) => (
          <MenuToggle
            ref={toggleRef}
            variant="plain"
            aria-label="Configurations"
            isExpanded={isConfigOpen}
            onClick={() => setIsConfigOpen(!isConfigOpen)}
            className="acm-page__config-toggle"
          >
            <CogIcon />
          </MenuToggle>
        )}
        popperProps={{
          appendTo: () => document.body,
          enableFlip: true,
          position: 'end',
        }}
      >
        <DropdownList>
          {SERVICES.map((service) => (
            <DropdownItem key={service} value={service}>
              {service}
            </DropdownItem>
          ))}
        </DropdownList>
        <Divider />
        <DropdownList>
          <DropdownItem value="get-login-command" icon={<ExternalLinkAltIcon />}>
            Get login command
          </DropdownItem>
          <DropdownItem value="add-repositories" icon={<CodeBranchIcon />}>
            Repositories
          </DropdownItem>
          <DropdownItem value="authentications" icon={<CodeBranchIcon />}>
            Authentications
          </DropdownItem>
        </DropdownList>
      </Dropdown>
    );
  }

  return (
    <FlexItem>
      <Toolbar>
        <ToolbarContent>
          <ToolbarGroup align={{ default: 'alignEnd' }}>
            <ToolbarItem>
              <Select
                toggle={serviceToggle}
                onSelect={onServiceSelect}
                selected={selectedService}
                isOpen={isServiceOpen}
                onOpenChange={(isOpen) => setIsServiceOpen(isOpen)}
              >
                {SERVICES.map((service) => (
                  <SelectOption key={service} value={service}>
                    {service}
                  </SelectOption>
                ))}
              </Select>
            </ToolbarItem>
            <ToolbarItem>
              <Dropdown
                toggle={actionsToggle}
                isOpen={isActionsOpen}
                onSelect={onActionsSelect}
                onOpenChange={(isOpen) => setIsActionsOpen(isOpen)}
                popperProps={{
                  appendTo: () => document.body,
                  enableFlip: true,
                  position: 'end',
                  minWidth: 'auto',
                  maxWidth: '200px',
                }}
              >
                <DropdownList>
                  <DropdownItem value="get-login-command" icon={<ExternalLinkAltIcon />}>
                    Get login command
                  </DropdownItem>
                  <DropdownItem value="add-repositories" icon={<CodeBranchIcon />}>
                    Repositories
                  </DropdownItem>
                  <DropdownItem value="authentications" icon={<CodeBranchIcon />}>
                    Authentications
                  </DropdownItem>
                </DropdownList>
              </Dropdown>
            </ToolbarItem>
          </ToolbarGroup>
        </ToolbarContent>
      </Toolbar>
    </FlexItem>
  );
};

export interface PageHeaderTitleProps {
  title: string;
  description: React.ReactNode;
  descriptionClassName?: string;
}

/** Page h1 plus the mobile configurations icon, aligned on one row. */
export const PageHeaderTitle: React.FunctionComponent<PageHeaderTitleProps> = ({
  title,
  description,
  descriptionClassName = 'acm-page__description',
}) => (
  <FlexItem className="acm-page__title-block">
    <div className="acm-page__title-row">
      <Title headingLevel="h1" size="xl">
        {title}
      </Title>
      <PageContextActions placement="title" />
    </div>
    {typeof description === 'string' ? (
      <Content component="p" className={descriptionClassName}>
        {description}
      </Content>
    ) : (
      description
    )}
  </FlexItem>
);
